import * as rxjs from 'rxjs';
import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Mutations from './Mutations';
import * as Relations from './Relations';
import * as Streams from './Streams';
import { Objects } from '.';

export function TallyValues<
    ValueType, 
    ResultType,
    DomainTypeOut extends Elevated.Domain<ResultType>
>(
    name: string,
    stream: Streams.ValueStream<ValueType, Elevated.Domain<ValueType>>,
    clear: () => ResultType,
    add: (accum: ResultType, value_: ValueType) => ResultType,
    remove: (accum: ResultType, value_: ValueType) => ResultType,
    domain: DomainTypeOut
): Streams.ScalarStream<ResultType, DomainTypeOut> {
    if(stream.scalar) {
        return Mutations.OfScalar.Tally(name, stream.scalar, clear, add, remove, domain);
    } else if(stream.set) {
        return Mutations.OfSet.Tally(name, stream.set, clear, add, remove, domain);
    } else if(stream.sequence) {
        return Mutations.OfSequence.Tally(name, stream.sequence, clear, add, remove, domain);
    } else if(stream.map) {
        return Mutations.OfMap.Tally(name, stream.map, clear, add, remove, domain);
    }
    throw new Error('Malformed Mutation')
}

export function Reduce<AccumType, BindingType extends Objects.BindingType>(
    name: string,
    stream: Streams.RelationStream<BindingType>,
    clear: () => AccumType,
    add: (accum: AccumType, entry: BindingType) => AccumType,
    remove: (accum: AccumType, entry: BindingType) => AccumType,
    domain: Elevated.Domain<AccumType>,
): Streams.ScalarStream<AccumType, Elevated.Domain<AccumType>> {    
    return Mutations.OfRelation.Reduce(name, stream, clear, add, remove, domain);
}
            

export function Map<ValueType, ResultType>(
    name: string,
    stream: Streams.ValueStream<ValueType, Elevated.Domain<ValueType>>,
    transform: (value_: undefined|ValueType) => ResultType,
    context: string,
    domain: Elevated.Domain<ResultType>
): Streams.ValueStream<ResultType, Elevated.Domain<ResultType>> {
    if(stream.scalar) {
        return { scalar: Mutations.OfScalar.Map(name, stream.scalar, transform, context, domain) };
    } else if(stream.set) {
        return { set: Mutations.OfSet.Map(name, stream.set, transform, context, domain) };
    } else if(stream.sequence) {
        return { sequence: Mutations.OfSequence.Map(name, stream.sequence, transform, context, domain) };
    } else if(stream.map) {
        return { map: Mutations.OfMap.Map(name, stream.map, transform, context, domain) };
    }
    return { };
}

export function Eval<
    ArgsType extends {[argName: string]: any }, 
    ScalarStreamsType extends {[argName in keyof ArgsType]: Streams.ScalarStream<any, Elevated.Domain<any>>},
    ResultType
>(
    name: string,
    domain: Elevated.Domain<ResultType>,
    function_: (args: ArgsType) => undefined|ResultType,
    streams_: Partial<ScalarStreamsType>
): Streams.ScalarStream<ResultType, Elevated.Domain<ResultType>> {
    const streams: Partial<ScalarStreamsType> = {};
    let missing = false;
    for(let key in streams_) {
        if(streams_[key] === undefined) missing = true;
        streams[key] = streams_[key];
    }
    if(missing) {
        return Clear<ResultType>(domain);
    }
    return Mutations.OfScalar.Eval<ArgsType, ScalarStreamsType, ResultType>(name, domain, function_, streams as ScalarStreamsType )
}

export function Collect<ValueType>(
    name: string,
    combine: (a: undefined|ValueType, b:undefined|ValueType) => undefined|ValueType,
    ...streams: Streams.ScalarStream<ValueType, Elevated.Domain<ValueType>>[]
): Streams.ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    return Mutations.OfScalar.Collect<ValueType>(name, combine, ...streams )
}

export function Scalar<ValueType>(
    value: ValueType
): Streams.ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    return Mutations.OfScalar.makeLiteral<ValueType>(value);
}

export function Clear<ValueType>(
    domain: Elevated.Domain<ValueType>
): Streams.ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    return Mutations.OfScalar.makeClear<ValueType>(domain);
}

export function Subject<ValueType>(
    domain: Elevated.Domain<ValueType>,
    subject: rxjs.BehaviorSubject<ValueType|undefined>
) {
    return Mutations.OfScalar.makeSubject(domain, subject);
}
